import { useEffect, useState } from 'react';
import { NextPageContext } from 'next';
import Head from 'next/head';
import { parseJSON, stringifyJSON } from 'common/utils';
import { ogpDataOfCourse } from 'common/utils/course';
import { generateCookieList } from 'common/utils/trackingTag';
import Icon from 'components/common/Icon';
import CourseDetail from 'components/CourseDetails';
import CoursePageSection from 'components/CoursePageSection';
import { SITE_BASE_URL } from 'config/env';
import { parse } from 'cookie';
import { useQuery } from 'react-query';
import { CourseInfo, getCourseDetail, LectureType } from 'service/course';
import useGlobalStore from 'store/global';

import styles from './CourseDetailPage.module.scss';

type CourseDetailsPageProps = {
  courseId: string;
  course: string | null;
};

export default function CourseDetailPage(
  props: CourseDetailsPageProps,
): JSX.Element {
  const courseId = props.courseId;
  const [courseInfo, setCourseInfo] = useState<CourseInfo>(
    parseJSON(props.course) as CourseInfo,
  );
  const [currentLecture, setCurrentLecture] = useState<LectureType>(() => {
    if (!courseInfo || !courseInfo.lectures) return;
    return courseInfo.lectures[0];
  });
  const isAuthInit = useGlobalStore((state) => state.isAuthInit);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const { isError, isLoading, isFetching, refetch } = useQuery(
    ['getCourseDetail', courseId?.toString()],
    async () => {
      if (!courseId) return Promise.reject();
      return getCourseDetail(courseId);
    },
    {
      enabled: isAuthInit && !!courseId,
      retry: false,
      onSuccess: (data) => {
        const course = data?.data;
        setCourseInfo(course);
        if (!currentLecture) {
          setCurrentLecture(course.lectures[0]);
        } else {
          const lecture = course.lectures.find(
            (lecture) => lecture.id === currentLecture?.id,
          );
          setCurrentLecture(lecture);
        }
      },
      refetchOnWindowFocus: false,
    },
  );
  useEffect(() => {
    if (currentAuth?.uid && isAuthInit) {
      void refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAuth?.uid]);
  const ogpData = ogpDataOfCourse(courseInfo);
  return (
    <>
      <Head>
        <title>{ogpData.title}</title>
        <meta name="description" content={ogpData.description} />

        <meta property="og:type" content="course" />
        <meta property="og:title" content={ogpData.title} />
        <meta property="og:description" content={ogpData.description} />
        {ogpData.imageUrl && (
          <meta
            property="og:image"
            key="og:image"
            content={ogpData.imageUrl || ''}
          />
        )}
        {(courseInfo?.benefits || []).length > 0 && (
          <meta
            name="keywords"
            content={(courseInfo?.benefits || []).join(', ')}
          />
        )}

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:url"
          content={`${SITE_BASE_URL}/courses/${courseId}`}
        />
        <meta name="twitter:title" content={ogpData.title} />
        <meta name="twitter:description" content={ogpData.description} />
        {ogpData.imageUrl && (
          <meta name="twitter:image" content={ogpData.imageUrl || ''} />
        )}
        <link rel="canonical" href={`${SITE_BASE_URL}/courses/${courseId}`} />
      </Head>
      <CoursePageSection>
        {isError && !isLoading && !isFetching ? (
          <div className={styles.emptyWrapper}>
            <Icon name="empty" width={96} height={96} />
            <div className={styles.emptyText}>このコースは存在しません</div>
          </div>
        ) : (
          courseInfo && (
            <CourseDetail
              courseInfo={courseInfo}
              refetchCourse={refetch}
              currentLecture={currentLecture}
              setCurrentLecture={setCurrentLecture}
            />
          )
        )}
      </CoursePageSection>
    </>
  );
}

export const getServerSideProps = async (context: NextPageContext) => {
  const courseId = context.query.courseId as string;
  const cookiesList = generateCookieList(
    context.query,
    parse(context.req?.headers?.cookie || ''),
  );
  if (cookiesList.length > 0) {
    context.res.setHeader('Set-Cookie', cookiesList);
  }

  let courseData: CourseInfo;
  try {
    courseData = (await getCourseDetail(courseId))?.data || ({} as CourseInfo);
    return {
      props: {
        courseId: courseId,
        course: stringifyJSON(courseData) as string,
      },
    };
  } catch (error) {
    return {
      props: {
        courseId: courseId,
        course: null,
      },
    };
  }
};
